/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { call, delay, put, race } from "redux-saga/effects";

import * as BenefitConstants from "../../Constants/BenefitConstants";
import * as visualStudioBenefitApi from "../../Services/VisualStudioBenefitApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { IVisualStudioBenefitsDataResult } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefitsDataResult";
import { IVisualStudioBenefitsRequest } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefitsRequest";
import { IVisualStudioBenefitsResult } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefitsResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import { IApiErrorResult, IApiResult2 } from "../../Interfaces/IApiResult";
import {
  downloadRequestsToCSVFail,
  downloadRequestsToCSVSuccess,
  searchVisualStudioBenefitsFail,
  searchVisualStudioBenefitsSuccess,
  IDownloadRequests,
  ISearchVisualStudioBenefits,
} from "./VisualStudioBenefitAction";

/**
 * Search all benefits for given text
 * @param {ISearchVisualStudioBenefits} action for searching benefits
 * @returns {void}
 */
function* handleSearchVisualStudioBenefits(action: ISearchVisualStudioBenefits) {
  const failedMessage = "Failed to search visual studio benefits.";
  const requestBody = {
    searchKey: action.searchText,
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
    awardPeriodList: [],
    benefitStatusList: [],
    expirationStartDate: undefined,
    expirationEndDate: undefined,
    tenant: action.tenant,
  } as unknown as IVisualStudioBenefitsRequest;

  action.selectedBenefitsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case BenefitConstants.BENEFIT_STATUS:
        requestBody.benefitStatusList.push(item.value);
        break;
      case BenefitConstants.AWARD_PERIOD:
        requestBody.awardPeriodList.push(item.value);
        break;
      case BenefitConstants.EXPIRATION_START_DATE:
        requestBody.expirationStartDate = new Date(item.value);
        break;
      case BenefitConstants.EXPIRATION_END_DATE:
        requestBody.expirationEndDate = new Date(item.value);
        break;
    }
  });

  try {
    const { result, timeout }: IApiResult2<IVisualStudioBenefitsDataResult> | any = yield race({
      result: call(visualStudioBenefitApi.searchVisualStudioBenefits, requestBody, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchVisualStudioBenefitsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IVisualStudioBenefitsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchVisualStudioBenefitsSuccess(
            parsedBody.vsBenefits,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          searchVisualStudioBenefitsFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(searchVisualStudioBenefitsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchVisualStudioBenefitsFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IDownloadRequests} action To download visual studio benefit requests.
 * @returns {void}
 */
function* handleDownloadRequestsToCSV(action: IDownloadRequests) {
  const defaultFailedErrorMessage = "Failed to download requetss";

  try {
    const { result, timeout } = yield race({
      result: call(visualStudioBenefitApi.downloadRequestsToCsv, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(downloadRequestsToCSVFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const data = yield result.text();

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(downloadRequestsToCSVSuccess(data));
        break;
      case HttpStatusCode.BadRequest:
        yield put(downloadRequestsToCSVFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(downloadRequestsToCSVFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(downloadRequestsToCSVFail(defaultFailedErrorMessage));
  }
}

export { handleDownloadRequestsToCSV, handleSearchVisualStudioBenefits };
